import { emptyApi } from './emptyApi'
import type {
  CenterApplicationInfoOut,
  CenterApplicationUpdateIn,
  CenterApplicationListParams,
  CenterApplicationEventInfoOut,
  CenterApplicationFormType,
  CenterApplicationSubmissionType,
  GeneralApiResponse
} from '../../types'

export const centerApplicationService = emptyApi.injectEndpoints({
  endpoints: (builder) => ({
    getCenterApplications: builder.query<
      CenterApplicationInfoOut[],
      CenterApplicationListParams
    >({
      query: (params) => ({
        url: '/admin/centers',
        params
      }),
      providesTags: (result, error, arg) => [
        { type: 'CenterApplication', id: `LIST_${arg.status || 'all'}` },
        'CenterApplication'
      ]
    }),

    getCenterApplication: builder.query<CenterApplicationInfoOut, number>({
      query: (id) => `/admin/center_application/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'CenterApplication', id }]
    }),

    getCenterApplicationEvents: builder.query<
      CenterApplicationEventInfoOut[],
      number
    >({
      query: (id) => `/admin/center_application_events/${id}`,
      providesTags: (_result, _error, id) => [
        { type: 'CenterApplicationEvent', id }
      ]
    }),

    updateCenterApplication: builder.mutation<
      CenterApplicationInfoOut,
      { id: number; data: CenterApplicationUpdateIn }
    >({
      query: ({ id, data }) => ({
        url: `/admin/center_application/${id}`,
        method: 'PATCH',
        body: data
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'CenterApplication', id },
        { type: 'CenterApplication', id: 'LIST_pending' },
        { type: 'CenterApplication', id: 'LIST_approved' },
        { type: 'CenterApplication', id: 'LIST_all' },
        'CenterApplication'
      ],
      // Force refetch of pending applications immediately after mutation
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          // Immediately refetch pending applications
          dispatch(
            centerApplicationService.endpoints.getCenterApplications.initiate(
              { status: 'pending' },
              { forceRefetch: true }
            )
          )
        } catch {
          // Error handled by RTK Query
        }
      }
    }),

    // Public endpoints for center application submission
    checkApplicationToken: builder.query<GeneralApiResponse, string>({
      query: (token) => `/admin/check_token/${token}`,
      providesTags: ['CenterApplication'],
      // Cache successful and failed responses to prevent repeated requests
      keepUnusedDataFor: 300, // Keep data for 5 minutes
      // Don't retry on 409 (token already used) or 404 (token not found)
      extraOptions: {
        maxRetries: 0 // Disable retries to prevent multiple 409s
      }
    }),

    submitCenterApplication: builder.mutation<
      GeneralApiResponse,
      CenterApplicationFormType
    >({
      query: (formData) => ({
        url: '/admin/center_application',
        method: 'POST',
        body: processCenterApplication(formData)
      }),
      invalidatesTags: ['CenterApplication']
    })
  })
})

// Helper function to transform form data to API format
function processCenterApplication(
  values: CenterApplicationFormType
): CenterApplicationSubmissionType {
  return {
    // Center Information
    center_name: values.centerName,
    street_address: values.streetAddress,
    city: values.city,
    state: values.state,
    zip_code: values.zipCode,
    director_first_name: values.directorFirstName,
    director_last_name: values.directorLastName,
    director_email: values.directorEmail,
    co_director_first_name: values.coDirectorFirstName,
    co_director_last_name: values.coDirectorLastName,
    co_director_email: values.coDirectorEmail,
    primary_center_contact_email: values.primaryCenterContactEmail,
    website_address: values.websiteAddress,
    center_type: values.centerType,
    application_token: values.applicationToken,

    // Center Components
    number_of_adult_sca: values.numberOfAdultSca,
    number_of_adult_sc: values.numberOfAdultSc,
    number_of_adult_other: values.numberOfAdultOther,
    number_of_pediatric_sca: values.numberOfPediatricSca,
    number_of_pediatric_sc: values.numberOfPediatricSc,
    number_of_pediatric_other: values.numberOfPediatricOther,
    number_of_dedicated_outpatient_staff:
      values.numberOfDedicatedOutpatientStaff,

    dedicated_clinic_space: values.dedicatedClinicSpace,
    indep_clinic_space: values.indepClinicSpace,
    dcs_shared_with_cancer_center: values.dcsSharedWithCancerCenter,
    dcs_shared_with_other: values.dcsSharedWithOther,
    dcs_shared_with_other_name: values.dcsSharedWithOtherName,

    infusion_area: values.infusionArea,
    inf_area_dedicated_day_hospital: values.infAreaDedicatedDayHospital,
    inf_area_shared_with_cancer_center: values.infAreaSharedWithCancerCenter,
    inf_area_shared_with_other: values.infAreaSharedWithOther,
    inf_area_shared_with_other_name: values.infAreaSharedWithOtherName,

    observation_unit: values.observationUnit,

    // Employee Information
    employees: {
      advanced_practice: values.advancedPractice
        ?.filter((item) => item.nameAndInfo !== '')
        .map((value) => ({
          name_and_info: value.nameAndInfo,
          employee_type: value.employeeType
        })),
      case_manager: values.caseManager
        ?.filter((item) => item.nameAndInfo !== '')
        .map((value) => ({
          name_and_info: value.nameAndInfo,
          employee_type: value.employeeType
        })),
      mental_health: values.mentalHealth
        ?.filter((item) => item.nameAndInfo !== '')
        .map((value) => ({
          name_and_info: value.nameAndInfo,
          employee_type: value.employeeType
        })),
      number_of_sickle_cell_nurses: values.numberOfSickleCellNurses,
      pharmacist: values.pharmacist
        ?.filter((item) => item.nameAndInfo !== '')
        .map((value) => ({
          name_and_info: value.nameAndInfo,
          employee_type: value.employeeType
        })),
      physical_therapist: values.physicalTherapist
        ?.filter((item) => item.nameAndInfo !== '')
        .map((value) => ({
          name_and_info: value.nameAndInfo,
          employee_type: value.employeeType
        })),
      primary_care_physician: values.primaryCarePhysician
        ?.filter((item) => item.nameAndInfo !== '')
        .map((value) => ({
          name_and_info: value.nameAndInfo,
          employee_type: value.employeeType
        })),
      sickle_cell_specialist: values.sickleCellSpecialist
        ?.filter((item) => item.nameAndInfo !== '')
        .map((value) => ({
          name_and_info: value.nameAndInfo,
          employee_type: value.employeeType
        })),
      social_worker: values.socialWorker
        ?.filter((item) => item.nameAndInfo !== '')
        .map((value) => ({
          name_and_info: value.nameAndInfo,
          employee_type: value.employeeType
        })),
      other_staff: values.otherStaff
        ?.filter((item) => item.nameAndInfo !== '')
        .map((value) => ({
          name_and_info: value.nameAndInfo,
          employee_type: value.employeeType
        }))
    },

    // Documentation and Protocols
    written_proc_for_sickle_cell_disease:
      values.writtenProcForSickleCellDisease,
    emergency_dept_protocols_for_scd: values.emergencyDeptProtocolsForScd,
    opioid_policy: values.opioidPolicy,
    infusion_policy: values.infusionPolicy,
    tracking_quality_metrics_protocol: values.trackingQualityMetricsProtocol,
    access_crizanlizumab_infusions: values.accessCrizanlizumabInfusions,
    access_prescribe_voxeletor: values.accessPrescribeVoxeletor,
    access_pharma_clinical_trials: values.accessPharmaClinicalTrials,
    access_stem_cell_transplant: values.accessStemCellTransplant,
    access_to_compound_hydroxyurea: values.accessToCompoundHydroxyurea,

    // Type-specific protocols
    rec_newly_trans_to_adult_protocol: values.recNewlyTransToAdultProtocol,
    trans_to_adult_protocol: values.transToAdultProtocol,
    tracking_newborn_screen_protocol: values.trackingNewbornScreenProtocol,
    pediatric_hematology: values.pediatricHematology,

    // Agreement and Signatures
    agree_to_grndad: values.agreeToGrndad,
    agree_to_bylaws: values.agreeToBylaws,
    signed_by: values.signedBy
  }
}

export const {
  useGetCenterApplicationsQuery,
  useGetCenterApplicationQuery,
  useGetCenterApplicationEventsQuery,
  useUpdateCenterApplicationMutation,
  useCheckApplicationTokenQuery,
  useSubmitCenterApplicationMutation
} = centerApplicationService
